'use client';

import React, { Fragment } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Hide } from '../ui/Icons';
import { cn } from '@/lib/utils';

export interface HeadlessModalProps {
  isOpen: boolean;
  closeModal: () => void;
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  dialogClassName?: string;
  dialogPanelClassName?: string;
  dialogCloseBtnClassName?: string;
  dialogWrapClassName?: string;
  dialogBodyClass?: string;
  showCloseIcon?: boolean;
  closeOnBackdropClick?: boolean;
}

export default function HeadlessModal({
  isOpen,
  closeModal,
  title,
  dialogPanelClassName,
  dialogCloseBtnClassName,
  dialogWrapClassName,
  children,
  dialogClassName,
  dialogBodyClass = '',
  showCloseIcon = true,
  closeOnBackdropClick = true,
}: HeadlessModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={cn('relative z-50', dialogClassName as string)}
        onClose={closeOnBackdropClick ? closeModal : () => {}}
        static={!closeOnBackdropClick}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className={cn('fixed inset-0 w-screen', dialogBodyClass)}>
          <div
            className={cn(
              'flex min-h-full items-center justify-center text-center md:p-4',
              dialogWrapClassName
            )}
          >
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={cn(
                  'relative min-h-screen w-full transform bg-white px-5 py-10 text-left shadow-xl transition-all md:min-h-fit md:w-[725px] md:rounded-2xl md:px-10',
                  dialogPanelClassName
                )}
              >
                {title && (
                  <DialogTitle
                    as="div"
                    className="text-h3 font-black text-title"
                  >
                    {title}
                  </DialogTitle>
                )}

                {showCloseIcon && (
                  <Hide
                    className={cn(
                      'absolute right-5 top-5 cursor-pointer text-neutral-400 transition-colors hover:text-primary-700',
                      dialogCloseBtnClassName
                    )}
                    onClick={() => closeModal()}
                  />
                )}
                {children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
